import { imageDataToBase64 } from '@/common/images';
import { GetUploadUrlMutationFn } from '@/common/utils/graphql.generated';
import { MediaType, ScanEventPhotoType } from '../../../../../backend/src/types.generated';
import { AddEditScanEventPhotoMutationFn } from './graphql.generated';

export interface PhotoUploadResult {
	success: boolean;
	photoId?: string;
	error?: string;
}

export class EditScanPhotoService {
	constructor(
		private getUploadUrlMutation: GetUploadUrlMutationFn,
		private addScanEventPhotoMutation: AddEditScanEventPhotoMutationFn
	) {}

	async uploadPhoto(
		imageData: ImageData,
		scanEventId: string,
		photoType: 'LOAD' | 'DOCKET'
	): Promise<PhotoUploadResult> {
		try {
			// Convert ImageData to base64
			const base64 = imageDataToBase64(imageData, 'jpeg', 0.8);
			const blob = this.base64ToBlob(base64, 'image/jpeg');

			// Generate unique S3 key for this photo
			const key = `scan-${scanEventId}-${photoType.toLowerCase()}-${Date.now()}.jpg`;

			// Request a signed upload URL from the backend
			const { data: uploadUrlData } = await this.getUploadUrlMutation({ variables: { key } });

			const uploadUrl = uploadUrlData?.getUploadUrl?.url;
			const filename = uploadUrlData?.getUploadUrl?.filename;

			if (!uploadUrl || !filename) {
				return { success: false, error: 'Failed to get upload URL' };
			}

			// Upload to S3
			const uploadResponse = await fetch(uploadUrl, {
				method: 'PUT',
				body: blob,
				headers: {
					'Content-Type': 'image/jpeg',
				},
			});

			if (!uploadResponse.ok) {
				return { success: false, error: 'Failed to upload to S3' };
			}

			// Link photo to scan event
			const photoResponse = await this.addScanEventPhotoMutation({
				variables: {
					input: {
						scanEvent: { id: scanEventId },
						photoType: photoType === 'LOAD' ? ScanEventPhotoType.Load : ScanEventPhotoType.Docket,
						photo: {
							filename,
							type: MediaType.Image,
						},
					},
				},
			});

			const photoId = photoResponse.data?.createScanEventPhoto?.id;

			if (!photoId) {
				return { success: false, error: 'Failed to create photo record' };
			}

			return { success: true, photoId };
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Photo upload failed:', error);
			return { success: false, error: 'Upload failed' };
		}
	}

	private base64ToBlob(base64: string, mimeType: string): Blob {
		const byteCharacters = atob(base64);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		return new Blob([byteArray], { type: mimeType });
	}
}
